import { z } from 'zod';
import { INVOICE_STATUSES, PAYMENT_METHODS } from '@shared/constants';
import {
  calendarDateSchema,
  centsSchema,
  nonEmptyString,
  optionalText,
  uuidSchema,
} from './common';

export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const paymentMethodSchema = z.enum(PAYMENT_METHODS);

/** A manual line item on an invoice. */
export const invoiceLineItemSchema = z.object({
  description: nonEmptyString,
  quantity: z
    .number({ message: 'Enter a quantity' })
    .nonnegative({ message: 'Quantity must not be negative' })
    .default(1),
  unitPriceCents: centsSchema,
});

const invoiceBase = z.object({
  customerId: uuidSchema,
  propertyId: uuidSchema.optional(),
  status: invoiceStatusSchema.optional(),
  issueDate: calendarDateSchema,
  dueDate: calendarDateSchema.optional(),
  customerMessage: optionalText,
  internalNotes: optionalText,
  jobIds: z.array(uuidSchema).optional(),
  items: z.array(invoiceLineItemSchema).optional(),
});

export const createInvoiceSchema = invoiceBase.refine(
  (data) => {
    const hasJobs = data.jobIds && data.jobIds.length > 0;
    const hasItems = data.items && data.items.length > 0;
    return hasJobs || hasItems;
  },
  { message: 'Add jobs to bill or enter line items', path: ['jobIds'] }
);

export const updateInvoiceSchema = invoiceBase
  .partial()
  .refine((data) => Object.keys(data).length > 0, { message: 'Nothing to update' });

export const recordPaymentSchema = z.object({
  amountCents: centsSchema.refine((v) => v > 0, {
    message: 'Payment must be more than zero',
  }),
  paymentMethod: paymentMethodSchema,
  paidAt: z.string({ message: 'Enter a date and time' }),
  reference: optionalText,
  notes: optionalText,
});

export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;
export type CreateInvoiceInput = z.infer<typeof createInvoiceSchema>;
export type UpdateInvoiceInput = z.infer<typeof updateInvoiceSchema>;
export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;
